import React, { useState, useEffect, useContext, useRef } from 'react';
import { array, func, string } from 'prop-types';
import classNames from 'classnames/bind';

import Hint from 'components/Hint';

import { withErrorBoundary } from 'containers/ErrorBoundary';

import CommonLabel from 'components/CommonLabel';
import PopinInfoContainer from 'containers/PopinInfoContainer';
import PopinKbaInfo from 'components/PopinKbaInfo';
import FieldNoResultsTooltip from 'components/FieldNoResultsTooltip';

import { StaticTextContext } from 'context/StaticTextContext';
import { SearchContext } from 'context/SearchContext';
import { CountryContext } from 'context/CountryContext';

import { usePartDetailsLazyQuery } from 'utils/getPartAutosuggestions';
import { getCarByKba } from 'utils/getCarByKba';
import { isBlock } from 'utils/browserHistory';

// import EuroIcon from 'media/icons/euro.icon.svg';
import CheckIcon from 'media/icons/checkmark.icon.svg';
import styles from './FieldSearchKba.module.scss';
const cx = classNames.bind(styles);

const FieldSearchKba = ({ pageType, setPartValue, setPartData, partData }) => {
  const [isNoResults, setIsNoResults] = useState(false);
  const [isError, setIsError] = useState(null);

  const { currentCountry } = useContext(CountryContext);
  const { labels } = useContext(StaticTextContext);
  const {
    setIsErrorPart,
    setIsSelectedKba,
    setManuName,
    setManuId,
    setModelName,
    setModelId,
    setCarName,
    setCarId,
    setPartName,
    setPartId,
    setKbaNumber,

    isSelectedKba,
    kbaNumber,
    carId,
    vehicleType,
  } = useContext(SearchContext);
  let kbrRef = useRef();

  const getPartDetails = usePartDetailsLazyQuery();

  const setClearSearchState = () => {
    // Update from props.
    setIsErrorPart(false);
    setPartValue('');
    setPartData([]);

    // Update context.
    setCarName('');
    setCarId(null);
    setModelName('');
    setModelId(null);
    setManuName('');
    setManuId(null);
    setPartName('');
    setPartId(null);
  };

  const handleOnBlur = (event) => {
    const currentValue = event.target.value;
    const trimmedValue = currentValue.replace(/-/g, '');

    setIsError(trimmedValue.length < 7 && currentValue.length > 0);
  };

  const handleOnFocus = () => {
    setIsError(false);
  };

  const partRequestSuccess = ({ status, data: response }) => {
    const partField = document.getElementById('search-part');
    if (status === 200 && response.status === 200) {
      const data = response.data.array ? response.data.array : [];
      setPartData(data);
      setIsErrorPart(false);

      if (partField && window.innerWidth > 768) {
        partField.focus();
      }
    }
  };

  const partSearchResultsSuccess = ({ status, data: response }) => {
    if (status === 200 && response.status === 200) {
      const data = response.data.array ? response.data.array : [];
      setPartData(data);
      setIsErrorPart(false);
    }
  };

  const getCarsByKbaSuccess = ({ status, data: response }) => {
    if (status === 200) {
      const currentCar = response.data.array && response.data.array[0];
      const carId = currentCar && currentCar.carId;

      if (carId) {
        setCarId(carId);
        setModelId(currentCar.modelId);
        setManuId(currentCar.manuId);
        setCarName(currentCar.carName);
        setIsSelectedKba(true);
        setIsNoResults(false);
      } else {
        setIsNoResults(true);
      }
    }
  };

  const handleOnChange = (event) => {
    const currentValue = event.target.value;
    const trimmedValue = currentValue.replace(/-/g, '');

    setClearSearchState();
    setPartData([]);
    setIsNoResults(false);
    setIsSelectedKba(false);
    setKbaNumber(currentValue);

    if (trimmedValue.length >= 7) {
      getCarByKba(currentValue, currentCountry)
        .then(getCarsByKbaSuccess)
        .catch(() => {
          setIsNoResults(false);
          setClearSearchState();
        });
    }
  };

  useEffect(() => {
    if (currentCountry !== '.com') {
      !isBlock && kbrRef.current && kbrRef.current.focus();
    }

    if (kbaNumber && !partData.length) {
      getPartDetails(carId, currentCountry, vehicleType).then(
        partSearchResultsSuccess
      );
    }
  }, [carId, kbaNumber, isBlock, currentCountry, vehicleType]);

  useEffect(() => {
    if (carId && pageType === 'home') {
      getPartDetails(carId, currentCountry, vehicleType).then(
        partRequestSuccess
      );
    }
  }, [carId, currentCountry, vehicleType]);

  return (
    <div className={cx('root')}>
      <div className={cx('buttons-group')}>
        <CommonLabel className={cx('label')} htmlFor="search-kbr">
          {labels.searchKbaLabel}
        </CommonLabel>

        <PopinInfoContainer>
          <PopinKbaInfo />
        </PopinInfoContainer>
      </div>

      <div
        className={cx('form-control-wrap', {
          'is-filled': isSelectedKba,
        })}
      >
        <input
          className={cx('form-control', {
            'is-error': isError,
            'is-filled': isSelectedKba,
          })}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          onChange={handleOnChange}
          type="text"
          id="search-kbr"
          minLength="4"
          ref={kbrRef}
          disabled={currentCountry === '.com'}
          value={kbaNumber}
          placeholder={labels.searchKbaPlaceholder}
          required={true}
          autoFocus={!isBlock && window.innerWidth > 768}
        />
        {isError && (
          <div className={cx('error-message', 'is-error')}>
            {labels.errorKbaNumber}
          </div>
        )}
        {isNoResults && (
          <FieldNoResultsTooltip
            title={labels.kbaTooltipTitle}
            description={labels.kbaTooltipDescription}
            className={cx('tooltip')}
          />
        )}
        {pageType === 'home' && (
          <Hint
            className={cx('hint', {
              'is-filled': isSelectedKba,
            })}
          >
            {!isSelectedKba ? (
              <span className={cx('hint-text')}>{labels.tipVinInput}</span>
            ) : (
              <span className={cx('hint-text')}>{labels.tipPartInput}</span>
            )}
          </Hint>
        )}
        <div className={cx('icon-check')}>
          <CheckIcon />
        </div>
      </div>
    </div>
  );
};

FieldSearchKba.propTypes = {
  pageType: string.isRequired,
  partData: array,
  setPartData: func.isRequired,
  setPartValue: func.isRequired,
};

FieldSearchKba.defaultProps = {
  partData: [],
};

export default withErrorBoundary(FieldSearchKba);



// WEBPACK FOOTER //
// ./src/components/SearchByKba/components/FieldSearchKba/FieldSearchKba.js